import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  Portfolio,
  PortfolioHistory,
  PortfolioHolding,
  PortfolioRange,
  tradingServices,
  TradingPosition,
} from '../../services/trading-service'
import { Skeleton } from '../../lib/components/Skeleton'
import { OrderTicket } from './order-ticket'
import { usd } from './order-rules'
import './trading.scss'

const RANGES: PortfolioRange[] = ['1D', '1W', '1M', '3M', '1Y']

const errorMessage = (res: any, fallback: string): string =>
  res?.data?.error?.message || res?.data?.message || fallback

const signedUsd = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(Math.abs(n))}`
const signedPct = (n: number | null) =>
  n === null ? '' : ` (${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}%)`
const tone = (n: number) => (n < 0 ? 'is-loss' : n > 0 ? 'is-gain' : '')
const pct = (n: number | null) => (n === null ? '—' : `${n.toFixed(1)}%`)

/** Ticker initials until a logo provider is chosen. */
const Monogram = ({ symbol }: { symbol: string }) => (
  <span className='trading-page__monogram' aria-hidden='true'>
    {symbol.slice(0, 2)}
  </span>
)

const tickFor = (range: PortfolioRange) => (t: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    ...(range === '1D'
      ? { hour: 'numeric', minute: '2-digit' }
      : range === '1Y'
      ? { month: 'short', year: '2-digit' }
      : { month: 'short', day: 'numeric' }),
  }).format(new Date(t))

const ValueChart = () => {
  const [range, setRange] = useState<PortfolioRange>('1M')
  const [history, setHistory] = useState<PortfolioHistory | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setHistory(null)
    setFailed(false)
    const load = async () => {
      const res = await tradingServices.getPortfolioHistory(range)
      if (cancelled) return
      if (res?.status === 200) setHistory(res.data)
      else setFailed(true)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [range])

  const points = history?.points ?? []
  const color = (history?.change ?? 0) < 0 ? '#b3261e' : '#0a7d3c'

  return (
    <section className='trading-page__card' data-testid='portfolio-chart'>
      <div className='trading-page__card-head'>
        <div>
          <h2>Portfolio value</h2>
          {history && history.change !== null ? (
            <p className={`trading-page__change ${tone(history.change)}`}>
              {signedUsd(history.change)}
              {signedPct(history.changePercent)} over {range}
            </p>
          ) : null}
        </div>
        <div className='trading-page__segmented is-small' role='group' aria-label='Chart range'>
          {RANGES.map((r) => (
            <button
              key={r}
              type='button'
              aria-pressed={range === r}
              className={range === r ? 'is-active' : ''}
              onClick={() => setRange(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className='trading-page__chart'>
        {failed ? (
          <p className='trading-page__error'>Portfolio history is unavailable right now.</p>
        ) : !history ? (
          <Skeleton width='100%' height='220px' />
        ) : points.length < 2 ? (
          <p className='trading-page__muted'>Not enough history for this range yet.</p>
        ) : (
          <ResponsiveContainer width='100%' height={220}>
            <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id='portfolioFill' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={color} stopOpacity={0.18} />
                  <stop offset='100%' stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey='t'
                tickFormatter={tickFor(range)}
                minTickGap={40}
                tick={{ fontSize: 12 }}
                stroke='#9aa0a6'
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                width={64}
                tick={{ fontSize: 12 }}
                stroke='#9aa0a6'
              />
              <Tooltip
                formatter={(v: number) => [usd(v), 'Value']}
                labelFormatter={(t: string) =>
                  new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/New_York',
                    dateStyle: 'medium',
                    timeStyle: range === '1D' || range === '1W' ? 'short' : undefined,
                  }).format(new Date(t))
                }
              />
              <Area
                type='monotone'
                dataKey='v'
                stroke={color}
                strokeWidth={2}
                fill='url(#portfolioFill)'
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  )
}

const HEADERS = [
  'Stock',
  'Shares',
  'Avg cost',
  'Price',
  'Market value',
  'Today',
  'Total P/L',
  'Allocation',
  '',
]

/** The order ticket works from positions; holdings carry the same facts. */
const asPosition = (h: PortfolioHolding): TradingPosition => ({
  symbol: h.symbol,
  qty: String(h.qty),
  qty_available: String(h.qtyAvailable),
  avg_entry_price: String(h.avgEntryPrice),
  market_value: String(h.marketValue),
  unrealized_pl: String(h.totalPl),
  unrealized_plpc: String((h.totalPlPercent ?? 0) / 100),
})

/**
 * Public markets portfolio: totals, value over time, and each holding with
 * day and total P/L and allocation. Rows link to the company page and offer
 * a quick Sell.
 */
const PortfolioContainer = () => {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null)
  const [error, setError] = useState('')
  const [selling, setSelling] = useState<PortfolioHolding | null>(null)
  const ticketRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    const res = await tradingServices.getPortfolio()
    if (res?.status === 200) {
      setPortfolio(res.data)
      setError('')
    } else {
      setError(
        res?.status === 404 || res?.status === 409
          ? 'NO_ACCOUNT'
          : errorMessage(res, 'Your portfolio is unavailable right now.'),
      )
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const sell = (h: PortfolioHolding) => {
    setSelling(h)
    window.setTimeout(
      () => ticketRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
      0,
    )
  }

  if (error === 'NO_ACCOUNT') {
    return (
      <div className='trading-page'>
        <h1>Portfolio</h1>
        <p className='trading-page__card'>
          You need an active brokerage account to hold stocks.{' '}
          <Link to='/dashboard/trading'>Open one on the trading page</Link>.
        </p>
      </div>
    )
  }

  const p = portfolio
  const stat = (label: string, value: React.ReactNode, className = '') => (
    <div>
      <span>{label}</span>
      <strong className={className}>{p ? value : <Skeleton width='110px' height='24px' />}</strong>
    </div>
  )

  return (
    <div className='trading-page is-wide' aria-busy={!p && !error}>
      <header className='trading-page__header'>
        <h1>Portfolio</h1>
        <p>
          <Link to='/dashboard/trading'>Trade stocks</Link>
          {' · '}
          <Link to='/dashboard/wallet-public'>Wallet</Link>
        </p>
      </header>

      {error ? (
        <p className='trading-page__error' role='alert'>
          {error}
        </p>
      ) : null}

      <section className='trading-page__stats' data-testid='portfolio-summary'>
        {stat('Portfolio value', usd(p?.equity))}
        {stat(
          'Today',
          p ? `${signedUsd(p.dayPl)}${signedPct(p.dayPlPercent)}` : null,
          p ? tone(p.dayPl) : '',
        )}
        {stat(
          'Total P/L',
          p ? `${signedUsd(p.totalPl)}${signedPct(p.totalPlPercent)}` : null,
          p ? tone(p.totalPl) : '',
        )}
        {stat('Cash', usd(p?.cash))}
      </section>

      <ValueChart />

      <section className='trading-page__card'>
        <h2>Holdings</h2>
        {!p ? (
          <Skeleton width='100%' height='120px' />
        ) : p.holdings.length === 0 ? (
          <p>
            You don&apos;t own any stocks yet. <Link to='/dashboard/trading'>Find one to buy</Link>.
          </p>
        ) : (
          <table className='trading-page__table' data-testid='portfolio-holdings'>
            <thead>
              <tr>
                {HEADERS.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {p.holdings.map((h) => (
                <tr key={h.symbol}>
                  <td>
                    <Link to={`/dashboard/trading/${h.symbol}`} className='trading-page__holding'>
                      <Monogram symbol={h.symbol} />
                      <span>
                        <strong>{h.symbol}</strong>
                        {h.name ? <span className='trading-page__muted'>{h.name}</span> : null}
                      </span>
                    </Link>
                  </td>
                  <td>{Number(h.qty.toFixed(6))}</td>
                  <td>{usd(h.avgEntryPrice)}</td>
                  <td>{usd(h.currentPrice)}</td>
                  <td>{usd(h.marketValue)}</td>
                  <td className={tone(h.dayPl)}>
                    {signedUsd(h.dayPl)}
                    <div className='trading-page__muted'>{signedPct(h.dayPlPercent)}</div>
                  </td>
                  <td className={tone(h.totalPl)}>
                    {signedUsd(h.totalPl)}
                    <div className='trading-page__muted'>{signedPct(h.totalPlPercent)}</div>
                  </td>
                  <td>
                    <span className='trading-page__allocation'>
                      <i style={{ width: `${Math.min(h.allocationPercent ?? 0, 100)}%` }} />
                    </span>
                    {pct(h.allocationPercent)}
                  </td>
                  <td>
                    <button
                      type='button'
                      className='trading-page__link'
                      onClick={() => sell(h)}
                      disabled={h.qtyAvailable <= 0}
                      aria-label={`Sell ${h.symbol}`}
                    >
                      Sell
                    </button>
                  </td>
                </tr>
              ))}
              <tr className='trading-page__cash-row'>
                <td>Cash</td>
                <td colSpan={3} />
                <td>{usd(p.cash)}</td>
                <td colSpan={2} />
                <td>{pct(p.cashAllocationPercent)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </section>

      {selling ? (
        <div ref={ticketRef}>
          <OrderTicket
            key={selling.symbol}
            selected={{ symbol: selling.symbol, name: selling.name || selling.symbol }}
            positions={p ? p.holdings.map(asPosition) : []}
            side='sell'
            onPlaced={() => void load()}
          />
        </div>
      ) : null}

      <p className='trading-page__disclaimer'>
        Values come from your brokerage account at Alpaca Securities LLC, member FINRA/SIPC.
        Today&apos;s change is the price move on shares you hold; money moved in or out does not
        count.
      </p>
    </div>
  )
}

export { PortfolioContainer }
